import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Building2, Plane, Search, ShieldAlert, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function NewComplaint() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [entity, setEntity] = useState(
    sp.get("id") ? { type: sp.get("type"), id: sp.get("id"), name: sp.get("name"), iata: sp.get("iata") } : null
  );
  const [pickQ, setPickQ] = useState("");
  const [pickRes, setPickRes] = useState(null);
  const [form, setForm] = useState({ category: "", title: "", body: "", incident_date: "" });
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/complaints/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    if (pickQ.trim().length < 2) { setPickRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q: pickQ } }).then((r) => setPickRes(r.data)), 250);
    return () => clearTimeout(t);
  }, [pickQ]);

  const submit = async () => {
    if (!entity) return toast.error("Select the airline or airport first");
    if (!form.category) return toast.error("Choose a category");
    if (form.title.trim().length < 8) return toast.error("Title must be at least 8 characters");
    if (form.body.trim().length < 30) return toast.error("Please add at least 30 characters of detail");
    if (!ack) return toast.error("You must accept the accuracy & fair-use declaration");
    setSubmitting(true);
    try {
      const { data } = await api.post("/complaints", {
        entity_type: entity.type, entity_id: entity.id, entity_name: entity.name, entity_iata: entity.iata || null,
        category: form.category, title: form.title, body: form.body, incident_date: form.incident_date || null, acknowledged: ack,
      });
      toast.success("Complaint submitted — pending moderation before publishing.");
      nav(`/complaints/${data.complaint_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="new-complaint-page" className="max-w-2xl mx-auto px-5 lg:px-8 py-10">
      <Link to="/complaints" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4"><ArrowLeft className="w-4 h-4" /> Forum</Link>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight">File a complaint</h1>
      <p className="mt-2 text-muted-foreground">Share a factual, first-hand experience. Submissions are moderated before publishing.</p>

      {/* Entity */}
      <div className="mt-8">
        <label className="block text-sm font-500 mb-2">Airline or airport</label>
        {entity ? (
          <div className="flex items-center justify-between border border-primary bg-blue-50 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-medium">
              {entity.type === "airport" ? <Building2 className="w-4 h-4 text-primary" /> : <Plane className="w-4 h-4 text-primary -rotate-45" />}
              {entity.name} {entity.iata && <span className="font-mono text-xs text-primary">{entity.iata}</span>}
            </span>
            <button onClick={() => setEntity(null)} className="text-xs text-primary hover:underline" data-testid="change-entity">Change</button>
          </div>
        ) : (
          <div className="relative">
            <div className="flex items-center border border-zinc-300 bg-white focus-within:ring-2 focus-within:ring-primary">
              <Search className="w-4 h-4 ml-3 text-muted-foreground" />
              <input value={pickQ} onChange={(e) => setPickQ(e.target.value)} placeholder="Search airline or airport…" data-testid="entity-search"
                className="w-full bg-transparent outline-none px-3 py-3" />
            </div>
            {pickRes && (pickRes.airlines?.length > 0 || pickRes.airports?.length > 0) && (
              <div className="absolute z-20 w-full bg-white border border-zinc-300 shadow-xl max-h-72 overflow-auto">
                {pickRes.airlines?.map((a) => (
                  <button key={`al${a.id}`} onClick={() => { setEntity({ type: "airline", id: a.id, name: a.name, iata: a.iata }); setPickRes(null); setPickQ(""); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-primary hover:text-white transition-colors">
                    <Plane className="w-4 h-4 -rotate-45" /> {a.name} <span className="font-mono text-xs ml-auto">{a.iata}</span>
                  </button>
                ))}
                {pickRes.airports?.map((a) => (
                  <button key={`ap${a.id}`} onClick={() => { setEntity({ type: "airport", id: a.id, name: a.name, iata: a.iata_code }); setPickRes(null); setPickQ(""); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-primary hover:text-white transition-colors">
                    <Building2 className="w-4 h-4" /> {a.name} <span className="font-mono text-xs ml-auto">{a.iata_code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-500 mb-2">Category</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="complaint-category"
            className="w-full border border-zinc-300 bg-white px-3 py-3 outline-none focus:ring-2 focus:ring-primary">
            <option value="">Select…</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-500 mb-2">Incident date (optional)</label>
          <input type="date" value={form.incident_date} onChange={(e) => setForm({ ...form, incident_date: e.target.value })} data-testid="complaint-date"
            className="w-full border border-zinc-300 bg-white px-3 py-3 outline-none focus:ring-2 focus:ring-primary" />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-500 mb-2">Title</label>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Summarise your complaint" data-testid="complaint-title"
          className="w-full border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary" />
      </div>

      <div className="mt-4">
        <label className="block text-sm font-500 mb-2">Details</label>
        <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} placeholder="What happened? Include flight/booking references and facts, not personal data of staff." data-testid="complaint-body"
          className="w-full border border-zinc-300 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-primary" />
      </div>

      <div className="mt-6 border border-amber-300 bg-amber-50 p-4 flex gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <label className="text-sm text-zinc-700">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="complaint-ack" className="mr-2 accent-primary" />
          I confirm this is a truthful, first-hand account. I will not post defamatory statements, threats, or personal data of individuals. I understand complaints are moderated and may be edited or removed.
        </label>
      </div>

      <button onClick={submit} disabled={submitting} data-testid="complaint-submit"
        className="mt-6 w-full bg-primary text-white py-3.5 font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60">
        {submitting ? "Submitting…" : "Submit complaint"}
      </button>
    </div>
  );
}
